import { Link, Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SiteLayout from '@/components/feature/SiteLayout'
import PageHeader from '@/components/feature/PageHeader'
import WeddingStoryDetailSeo from '@/components/feature/WeddingStoryDetailSeo'
import PhotoGridSection from '@/components/feature/sections/PhotoGridSection'
import RelatedStoriesSection from '@/components/feature/sections/RelatedStoriesSection'
import CtaBand from '@/components/feature/CtaBand'
import { weddingStoryDetails } from '@/mocks/weddingStories'

export default function WeddingStoryDetail() {
  const { t } = useTranslation()
  const { slug } = useParams()
  const story = weddingStoryDetails.find((item) => item.slug === slug)

  if (!story) {
    return <Navigate to="/wedding-stories" replace />
  }

  const portrait = story.images[0]

  return (
    <SiteLayout>
      <WeddingStoryDetailSeo story={story} />
      <PageHeader
        breadcrumb={story.couple}
        label={t('weddingStories.label')}
        title={story.couple}
        description={story.excerpt}
        image={story.coverImage}
        imageAlt={`${story.couple} — ${story.type} in ${story.place} photographed by Shabi Media`}
      />

      <section className="w-full bg-background-50 py-20 md:py-28">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-primary-500" />
                <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                  {story.type} • {story.place}
                </span>
              </div>

              <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
                {t('weddingStories.detail.storyLabel')}
              </h2>

              {story.paragraphs.map((para) => (
                <p key={para.slice(0, 24)} className="mt-5 text-base text-foreground-600 leading-relaxed">
                  {para}
                </p>
              ))}

              <div className="mt-9 rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-8 bg-accent-500" />
                  <span className="section-label text-accent-700 text-[11px] md:text-xs font-medium">
                    {t('weddingStories.detail.factsLabel')}
                  </span>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {story.facts.map((fact) => (
                    <div key={fact.label} className="flex items-start gap-3">
                      <span className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-accent-100 text-accent-700">
                        <i className={`${fact.icon} text-lg`} />
                      </span>
                      <div>
                        <dt className="text-[11px] tracking-[0.12em] uppercase text-foreground-500">{fact.label}</dt>
                        <dd className="text-sm font-medium text-foreground-800 mt-0.5 leading-snug">{fact.value}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden border border-background-200 h-72 md:h-96">
                <img
                  src={portrait.image}
                  alt={`${portrait.title} — ${story.couple}'s ${story.type} in ${story.place}`}
                  title={`${portrait.title} | Shabi Media wedding photography, ${story.place}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <figure className="mt-5 rounded-lg border border-background-200 bg-primary-950 p-6 md:p-7">
                <div className="flex items-center justify-between mb-4">
                  <i className="ri-double-quotes-l text-primary-400 text-3xl" />
                  <div className="flex items-center gap-1 text-primary-400">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <i key={i} className="ri-star-fill text-sm" />
                    ))}
                  </div>
                </div>
                <blockquote className="font-heading italic text-lg md:text-xl text-background-100 leading-relaxed">
                  {story.review.quote}
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-background-50/15 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center bg-accent-500 text-background-50 font-heading text-lg font-semibold">
                    {story.review.name.charAt(0)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-background-50">{story.review.name}</span>
                    <span className="block text-xs text-background-300 mt-0.5">{story.review.role}</span>
                  </span>
                </figcaption>
              </figure>

              <Link
                to="/wedding-stories"
                className="mt-5 inline-flex items-center justify-center gap-2 h-12 px-7 w-full rounded-md border border-background-300 text-foreground-800 text-sm font-medium tracking-wide whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-700 transition-colors"
              >
                <i className="ri-arrow-left-line text-lg" />
                {t('weddingStories.backToHub')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-20 md:py-28">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mb-12 md:mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-500" />
              <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                {t('weddingStories.detail.timelineLabel')}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
              {t('weddingStories.detail.timelineTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {story.timeline.map((step) => (
              <article key={step.step} className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
                <div className="flex items-center justify-between mb-4">
                  <span className="w-12 h-12 rounded-full flex items-center justify-center bg-primary-100 text-primary-700">
                    <i className={`${step.icon} text-xl`} />
                  </span>
                  <span className="font-heading text-3xl font-semibold text-background-300">{step.step}</span>
                </div>
                <span className="text-[11px] tracking-[0.16em] uppercase text-accent-700 font-medium">{step.time}</span>
                <h3 className="mt-2 font-heading text-xl md:text-2xl font-semibold text-foreground-950 leading-snug">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <PhotoGridSection
        label={t('weddingStories.detail.galleryLabel')}
        title={t('weddingStories.detail.galleryTitle')}
        intro={t('weddingStories.detail.galleryIntro')}
        items={story.images}
      />

      <RelatedStoriesSection currentSlug={story.slug} />

      <CtaBand
        eyebrow={t('weddingStories.detail.ctaEyebrow')}
        title={t('weddingStories.detail.ctaTitle')}
        description={t('weddingStories.detail.ctaDescription')}
      />
    </SiteLayout>
  )
}